import {
  Action,
  ActionIcon, FormHandlers, ICON_PENCIL_OUTLINE, ICON_PLUS, Portal, useModal,
} from '@doveit/bricks';
import React from 'react';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { PropertyUnit } from '../../../providers/api/dtos';
import { toPropertyUnit, toPropertyUnitFormModel } from '../../components/property-unit-form/mappers';
import PropertyUnitForm, { PropertyUnitFormModel } from '../../components/property-unit-form/PropertyUnitForm';

interface ChildFnProps {
  upsert: VoidFunction,
}

interface ForCreate {
  propertyUnit?: undefined,
  onSuccess?: (upsertedPropertyUnit: PropertyUnit) => Promise<void> | void,
}

interface ForUpdate {
  propertyUnit: PropertyUnit,
  onSuccess?: (upsertedPropertyUnit: PropertyUnit, oldPropertyUnit: PropertyUnit) => Promise<void> | void,
}

export type UpsertPropertyUnitActionProps = (ForCreate | ForUpdate) & React.AriaAttributes & {
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
};

const UpsertPropertyUnitAction: React.FC<UpsertPropertyUnitActionProps> = ({
  propertyUnit,
  onSuccess,
  children,
  ...rest
}) => {
  const modal = useModal();
  const [isSaving, setIsSaving] = React.useState(false);
  const formRef = React.useRef<FormHandlers>() as React.MutableRefObject<FormHandlers>;

  const onSubmit = React.useCallback(async (values: PropertyUnitFormModel) => {
    setIsSaving(true);

    const upsertedPropertyUnit = toPropertyUnit(values);

    if (propertyUnit) {
      await onSuccess?.(upsertedPropertyUnit, propertyUnit);
    } else {
      await onSuccess?.(upsertedPropertyUnit);
    }

    modal.close();

    setIsSaving(false);
  }, [onSuccess, propertyUnit, modal]);

  return (
    <>
      {children
        ? children({
          upsert: () => modal.open(),
        })
        : (
          <ActionIcon
            icon={propertyUnit
              ? { path: ICON_PENCIL_OUTLINE }
              : { path: ICON_PLUS }}
            label={propertyUnit
              ? 'Modifica'
              : 'Aggiungi'}
            aria-label={
              propertyUnit
                ? "Pulsante per modificare l'unità immobiliare"
                : "Pulsante per aggiungere un'unità immobiliare"
            }
            size="S"
            onClick={() => modal.open()}
            {...rest}

          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title={
            propertyUnit
              ? 'Modifica unità immobiliare'
              : 'Aggiungi unità immobiliare'
          }
          aria-label={
            propertyUnit
              ? "Modale per modificare l'unità immobiliare"
              : "Modale per aggiungere un'unità immobiliare"
          }
          footer={(
            <Action
              label={
                propertyUnit
                  ? 'Modifica'
                  : 'Aggiungi'
              }
              color="primary"
              emphasis="high"
              onClick={() => formRef.current?.handleSubmit()}
              loading={isSaving}
            />
          )}
        >
          <PropertyUnitForm
            onSubmit={onSubmit}
            initialValues={propertyUnit
              ? toPropertyUnitFormModel(propertyUnit)
              : {}}
            innerRef={formRef}
            hideSubmit
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpsertPropertyUnitAction;
